import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCopy,
  faPaperPlane,
  faRobot,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { Icon24 } from "../../../components/ui";

const markdownComponents = {
  p: ({ children }) => <p className="markdown-p">{children}</p>,
  ul: ({ children }) => <ul className="markdown-ul">{children}</ul>,
  ol: ({ children }) => <ol className="markdown-ol">{children}</ol>,
  li: ({ children }) => <li className="markdown-li">{children}</li>,
  strong: ({ children }) => (
    <strong className="markdown-strong">{children}</strong>
  ),
  em: ({ children }) => <em className="markdown-em">{children}</em>,
  code: ({ children }) => <code className="markdown-code">{children}</code>,
  blockquote: ({ children }) => (
    <blockquote className="markdown-blockquote">{children}</blockquote>
  ),
  del: ({ children }) => <>{children}</>,
};

const getVisibleAssistantMessage = (content, expanded) => {
  const full = content || "";
  const limit = 900;
  if (expanded || full.length <= limit) return full;
  return `${full.slice(0, limit)}...`;
};

const StudyEditChatPanel = ({
  chatEndRef,
  chatInput,
  chatInputRef,
  chatMessages,
  copiedIndex,
  isChatLoading,
  isChatMinimized,
  isExpanded,
  sendChatMessage,
  setChatInput,
  setCopiedIndex,
  setIsChatMinimized,
  setToast,
  toggleExpand,
}) => (
  <>
    {!isChatMinimized && (
      <div className="chat-section">
        <div className="chat-container">
          <div className="chat-header" onClick={() => false}>
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <FontAwesomeIcon icon={faRobot} />
              <span>AI 연구 도우미</span>
            </div>
            <button
              className="chat-minimize-btn"
              onClick={(event) => {
                event.stopPropagation();
                setIsChatMinimized(true);
              }}
              title="최소화"
            >
              <Icon24 name="floating" />
            </button>
          </div>

          <div className="chat-messages">
            {chatMessages.map((message, index) => (
              <div key={index} className={`chat-message ${message.type}`}>
                <div
                  className="message-content"
                  style={{ position: "relative", marginBottom: 10 }}
                >
                  {message.type === "assistant" ? (
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={markdownComponents}
                    >
                      {getVisibleAssistantMessage(
                        message.content,
                        isExpanded(index)
                      )}
                    </ReactMarkdown>
                  ) : (
                    message.content
                  )}

                  {message.type === "assistant" &&
                    message.content &&
                    message.content.length > 900 && (
                      <button
                        onClick={() => toggleExpand(index)}
                        style={{
                          position: "absolute",
                          left: 6,
                          bottom: 6,
                          border: "none",
                          background: "transparent",
                          color: "#2563eb",
                          cursor: "pointer",
                          fontSize: 12,
                          padding: 4,
                        }}
                      >
                        {isExpanded(index) ? "접기" : "더보기"}
                      </button>
                    )}

                  {message.type === "assistant" &&
                    index !== 0 &&
                    message.content &&
                    message.content.trim() && (
                      <button
                        aria-label="복사"
                        title={copiedIndex === index ? "복사됨" : "복사"}
                        onClick={async () => {
                          try {
                            await navigator.clipboard.writeText(
                              message.content || ""
                            );
                            setCopiedIndex(index);
                            setToast({
                              isOpen: true,
                              type: "success",
                              message: "클립보드에 복사되었습니다!",
                            });
                            setTimeout(() => setCopiedIndex(null), 1200);
                          } catch (error) {
                            setToast({
                              isOpen: true,
                              type: "error",
                              message: "복사에 실패했습니다",
                            });
                          }
                        }}
                        style={{
                          position: "absolute",
                          right: 6,
                          bottom: 6,
                          border: "none",
                          background: "#f3f4f6",
                          borderRadius: 6,
                          padding: 8,
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: 28,
                          height: 28,
                        }}
                      >
                        <FontAwesomeIcon
                          icon={faCopy}
                          style={{ color: "#6b7280" }}
                        />
                      </button>
                    )}
                </div>
              </div>
            ))}

            {isChatLoading && (
              <div className="chat-message assistant">
                <div className="message-content">
                  <FontAwesomeIcon icon={faSpinner} spin />
                  <span>답변 생성 중...</span>
                </div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>

          <div className="chat-input-container">
            <textarea
              ref={chatInputRef}
              className="chat-input"
              placeholder="궁금한 점을 물어보세요..."
              value={chatInput}
              onChange={(event) => {
                setChatInput(event.target.value);
              }}
              onKeyPress={(event) => {
                if (event.key === "Enter" && !event.shiftKey) {
                  event.preventDefault();
                  sendChatMessage();
                }
              }}
              disabled={isChatLoading}
              rows={1}
              style={{
                resize: "none",
                minHeight: "44px",
                maxHeight: "120px",
                overflowY: "auto",
              }}
            />
            <button
              className="chat-send-btn"
              onClick={sendChatMessage}
              disabled={!chatInput.trim() || isChatLoading}
            >
              <FontAwesomeIcon icon={faPaperPlane} />
            </button>
          </div>
        </div>
      </div>
    )}

    {isChatMinimized && (
      <div className="chat-minimized-window">
        <div
          className="chat-minimized-header"
          onClick={() => setIsChatMinimized(false)}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <FontAwesomeIcon icon={faRobot} />
            <span>AI 연구 도우미</span>
          </div>
          <button
            className="chat-restore-btn"
            onClick={(event) => {
              event.stopPropagation();
              setIsChatMinimized(false);
            }}
            title="확장"
          >
            <Icon24 name="folding" />
          </button>
        </div>
      </div>
    )}
  </>
);

export default StudyEditChatPanel;
